import React, { useState } from "react";
import { streamSSE } from "@/lib/api";
import Markdown from "@/components/Markdown";
import { toast } from "sonner";
import { Sparkles, Loader2, Compass, Wand2, Layers } from "lucide-react";

const CLASS_COLORS = {
  "1": { badge: "border-emerald-400/50 bg-emerald-500/10 text-emerald-200", label: "Class I" },
  "2": { badge: "border-amber-400/50 bg-amber-500/10 text-amber-200", label: "Class II" },
  "3": { badge: "border-rose-400/50 bg-rose-500/10 text-rose-200", label: "Class III" },
};

export default function ClassificationWizard() {
  const [intendedUse, setIntendedUse] = useState("");
  const [features, setFeatures] = useState("");
  const [description, setDescription] = useState("");
  const [candidates, setCandidates] = useState([]);
  const [predicates, setPredicates] = useState([]);
  const [analysis, setAnalysis] = useState("");
  const [streaming, setStreaming] = useState(false);

  const run = async () => {
    if (intendedUse.trim().length < 5) {
      toast.error("Describe the intended use in a sentence or two.");
      return;
    }
    setStreaming(true);
    setCandidates([]);
    setPredicates([]);
    setAnalysis("");
    let buf = "";
    try {
      await streamSSE(
        "/classify/device",
        {
          intended_use: intendedUse,
          device_features: features,
          description,
        },
        (frame) => {
          if (frame.candidates) setCandidates(frame.candidates);
          if (frame.top_predicates) setPredicates(frame.top_predicates);
          if (frame.delta) {
            buf += frame.delta;
            setAnalysis(buf);
          }
          if (frame.error) toast.error(frame.error);
        }
      );
    } catch (e) {
      toast.error("Classification failed: " + e.message);
    } finally {
      setStreaming(false);
    }
  };

  return (
    <div data-testid="classify-root" className="max-w-6xl">
      <div className="mb-6">
        <div className="overline mb-2 text-[#2563EB]">CLASSIFICATION WIZARD · openFDA + Claude Sonnet 4.5</div>
        <h1 className="font-serif text-3xl font-semibold tracking-tight lg:text-4xl">
          Where does your device fit?
        </h1>
        <p className="mt-2 max-w-2xl text-sm text-slate-500">
          Describe the intended use. Qualmitta pulls candidate product codes from openFDA's device
          classification database, ranks predicates from the live 510(k) index, and returns a class,
          regulation number, and submission pathway you can defend to a reviewer.
        </p>
      </div>

      <div className="grid gap-6 lg:grid-cols-12">
        {/* Inputs */}
        <div className="lg:col-span-5 space-y-4">
          <label className="block">
            <div className="overline mb-1.5 text-slate-500">INTENDED USE</div>
            <textarea
              data-testid="classify-intended-use"
              value={intendedUse}
              onChange={(e) => setIntendedUse(e.target.value)}
              rows={3}
              placeholder="A continuous glucose monitor for adults with type 2 diabetes that…"
              className="w-full border border-slate-200 bg-white p-3 text-sm text-slate-900 placeholder:text-slate-400 focus:border-[#2563EB] focus:outline-none"
            />
          </label>
          <label className="block">
            <div className="overline mb-1.5 text-slate-500">KEY FEATURES / TECHNOLOGY</div>
            <textarea
              data-testid="classify-features"
              value={features}
              onChange={(e) => setFeatures(e.target.value)}
              rows={2}
              placeholder="e.g. non-invasive optical sensor, wireless BLE, ML-based trend prediction"
              className="w-full border border-slate-200 bg-white p-3 text-sm text-slate-900 placeholder:text-slate-400 focus:border-[#2563EB] focus:outline-none"
            />
          </label>
          <label className="block">
            <div className="overline mb-1.5 text-slate-500">DESCRIPTION (optional)</div>
            <textarea
              data-testid="classify-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              placeholder="Anything else that helps disambiguate the product code."
              className="w-full border border-slate-200 bg-white p-3 text-sm text-slate-900 placeholder:text-slate-400 focus:border-[#2563EB] focus:outline-none"
            />
          </label>

          <button
            data-testid="classify-run-btn"
            onClick={run}
            disabled={streaming}
            className="inline-flex w-full items-center justify-center gap-2 bg-[#2563EB] px-5 py-3 text-sm font-semibold text-white transition-all hover:bg-[#1D4ED8] disabled:opacity-40"
          >
            {streaming ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wand2 className="h-4 w-4" />}
            {streaming ? "Classifying…" : "Run classification"}
          </button>

          {/* Candidates */}
          {candidates.length > 0 && (
            <div data-testid="classify-candidates" className="mt-6 border border-slate-200 bg-white">
              <div className="border-b border-slate-200 px-4 py-2">
                <div className="overline text-[#2563EB]">CANDIDATE PRODUCT CODES · openFDA</div>
              </div>
              <ul className="divide-y divide-slate-200 text-xs">
                {candidates.map((c, i) => {
                  const color = CLASS_COLORS[String(c.device_class || "").trim()] || {
                    badge: "border-slate-200 bg-slate-50 text-slate-600",
                    label: `Class ${c.device_class || "?"}`,
                  };
                  return (
                    <li key={i} data-testid="classify-candidate-row" className="p-3">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="border border-[#2563EB]/50 bg-[#2563EB]/10 px-1.5 py-0.5 font-mono text-[11px] font-semibold text-[#93C5FD]">
                          {c.product_code}
                        </span>
                        <span className={`border ${color.badge} px-1.5 py-0.5 text-[10px] font-semibold`}>
                          {color.label}
                        </span>
                        {c.regulation_number && (
                          <span className="text-[11px] text-slate-500">21 CFR {c.regulation_number}</span>
                        )}
                        {c.submission_type_id === "2" && (
                          <span className="border border-rose-400/40 bg-rose-500/10 px-1.5 py-0.5 text-[10px] font-semibold text-rose-200">
                            PMA
                          </span>
                        )}
                        {c.submission_type_id === "1" && (
                          <span className="border border-emerald-400/40 bg-emerald-500/10 px-1.5 py-0.5 text-[10px] font-semibold text-emerald-200">
                            510(k)
                          </span>
                        )}
                      </div>
                      <div className="mt-1 font-medium text-slate-800">{c.device_name}</div>
                      {c.definition && (
                        <div className="mt-1 text-[11px] text-slate-500 line-clamp-2">{c.definition}</div>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          )}

          {predicates.length > 0 && (
            <div data-testid="classify-predicates" className="border border-slate-200 bg-white">
              <div className="flex items-center gap-2 border-b border-slate-200 px-4 py-2">
                <Layers className="h-3.5 w-3.5 text-[#2563EB]" />
                <span className="overline text-[#2563EB]">RECENT PREDICATES FOR TOP CODE</span>
              </div>
              <ul className="divide-y divide-slate-200 text-xs">
                {predicates.map((p, i) => (
                  <li key={i} className="p-3">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="border border-slate-200 bg-slate-50 px-1.5 py-0.5 font-mono text-[11px] text-slate-700">
                        {p.k_number}
                      </span>
                      <span className="text-[11px] text-slate-500">cleared {p.decision_date || "—"}</span>
                    </div>
                    <div className="mt-1 text-slate-800">{p.device_name}</div>
                    <div className="text-[11px] text-slate-500">{p.applicant}</div>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* Analysis */}
        <div className="lg:col-span-7">
          <div className="min-h-[420px] border border-slate-200 bg-white">
            <div className="flex items-center gap-2 border-b border-slate-200 px-4 py-3">
              <Compass className="h-4 w-4 text-[#2563EB]" />
              <span className="overline text-[#2563EB]">CLASSIFICATION ANALYSIS</span>
              {streaming && <Loader2 className="ml-auto h-4 w-4 animate-spin text-slate-500" />}
            </div>
            <div data-testid="classify-analysis" className="max-h-[70vh] overflow-y-auto p-6">
              {analysis ? (
                <Markdown>{analysis}</Markdown>
              ) : (
                <div className="flex h-[360px] flex-col items-center justify-center text-center text-slate-400">
                  <Sparkles className="mb-3 h-6 w-6 text-[#2563EB]" strokeWidth={1.5} />
                  <div className="text-sm">Describe the device on the left, then hit <b className="text-slate-600">Run classification</b>.</div>
                  <div className="mt-1 text-xs text-slate-300">
                    Qualmitta will surface the most likely CDRH product code, class, regulation number and 510(k) predicates.
                  </div>
                </div>
              )}
            </div>
            <div className="border-t border-slate-200 px-4 py-2 text-[11px] text-slate-400">
              Screening only — confirm class and pathway with CDRH via a Q-Sub or pre-submission meeting.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
